import { fleetApiHttp } from '@/services/api/tattle-http'
import type { MySweepResponse, StartSweepResult } from './my-work-sweep.types'
import type {
  AutoMergeRequest,
  AutoMergeResponse,
  AnswerDecisionRequest,
  AnswerDecisionResult,
  MyActionsResponse,
  MachineThanks,
  MergePrsRequest,
  MergePrsResponse,
  MergePreviewResponse,
  MergeSettings,
  MyWork,
  MyWorkCounts,
  PrDetailsResponse,
  PrRefreshResponse,
  SaveMergeSettingsRequest,
  StartActionRequest,
  StartActionResult
} from './my-work.types'

/** The fleet decides who the person is from their token, so no call names a person. */
export const MyWorkApi = {
  async getMyWork(refresh = false) {
    const response = await fleetApiHttp.get<MyWork>(
      '/me',
      refresh ? { params: { refresh: 1 } } : undefined
    )
    return response.data
  },

  /** Each engineer's laptop runs; `owners` is absent from a fleet service older than runner health. */
  async listRunnerOwners() {
    const response = await fleetApiHttp.get<{ owners?: MachineThanks[] }>('/runners')
    return response.data.owners ?? []
  },

  async listActions() {
    const response = await fleetApiHttp.get<MyActionsResponse>('/me/actions')
    return response.data
  },

  async startAction(request: StartActionRequest) {
    const response = await fleetApiHttp.post<StartActionResult>('/me/actions', request)
    return response.data
  },

  async answerDecision(answer: AnswerDecisionRequest) {
    const response = await fleetApiHttp.post<AnswerDecisionResult>('/me/decisions', answer)
    return response.data
  },

  async getSweep() {
    const response = await fleetApiHttp.get<MySweepResponse>('/me/sweep')
    return response.data
  },

  async startSweep() {
    const response = await fleetApiHttp.post<StartSweepResult>('/me/sweep', {})
    return response.data
  },

  async mergePrs(request: MergePrsRequest) {
    const response = await fleetApiHttp.post<MergePrsResponse>('/me/merge', request)
    return response.data
  },

  async setAutoMerge(request: AutoMergeRequest) {
    const response = await fleetApiHttp.post<AutoMergeResponse>('/me/auto-merge', request)
    return response.data
  },

  async getMergeSettings() {
    const response = await fleetApiHttp.get<MergeSettings>('/me/merge-settings')
    return response.data
  },

  async saveMergeSettings(request: SaveMergeSettingsRequest) {
    const response = await fleetApiHttp.post<MergeSettings>('/me/merge-settings', request)
    return response.data
  },

  /** Judges the person's open PRs by settings not yet saved. A POST for its body; it saves nothing. */
  async previewMergeSettings(request: SaveMergeSettingsRequest) {
    const response = await fleetApiHttp.post<MergePreviewResponse>('/me/merge-preview', request)
    return response.data
  },

  /** Reads one PR again on its own, a few Bitbucket calls instead of the whole page's. */
  async refreshPr(repo: string, id: number) {
    const response = await fleetApiHttp.post<PrRefreshResponse>(
      `/me/prs/${encodeURIComponent(repo)}/${id}/refresh`
    )
    return response.data
  },

  /** A folded group's rows, as `repo#id`; the fleet reads only rows it left unread on the person's page. */
  async getPrDetails(targets: string[]) {
    const response = await fleetApiHttp.get<PrDetailsResponse>('/me/details', { params: { prs: targets.join(',') } })
    return response.data
  },

  async getCounts() {
    const response = await fleetApiHttp.get<MyWorkCounts>('/me/counts')
    return response.data
  }
}
